import { useEffect, useState } from 'react';
import api from '../api/axios';
import styles from './Table.module.css';

const SERVER_URL = window.location.origin;
const imgUrl = (p) => p ? `${SERVER_URL}${p.startsWith('/') ? p : `/${p}`}` : '';

export default function Reports() {
  const [reports, setReports]       = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [statusFilter, setStatus]   = useState('');
  const [typeFilter, setType]       = useState('');
  const [loading, setLoading]       = useState(false);

  const load = async (p = page, status = statusFilter, type = typeFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 20 });
      if (status) params.append('status', status);
      if (type)   params.append('type', type);
      const res = await api.get(`/reports/admin?${params}`);
      setReports(res.data.reports);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(page, statusFilter, typeFilter); }, [page, statusFilter, typeFilter]); // eslint-disable-line

  const handleReview = async (id) => {
    try { await api.patch(`/reports/admin/${id}/review`); load(); }
    catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  const handleBlock = async (id) => {
    if (!window.confirm('Block this user?')) return;
    try { await api.patch(`/reports/admin/${id}/block`); load(); }
    catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  const handleRemoveAd = async (id) => {
    if (!window.confirm('Deactivate this ad?')) return;
    try { await api.patch(`/reports/admin/${id}/remove-ad`); load(); }
    catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  const statusBadge = (s) => {
    const colors = { pending: '#f59e0b', reviewed: '#6366f1', blocked: '#ef4444' };
    return (
      <span style={{ background: colors[s] || '#ccc', color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
        {s}
      </span>
    );
  };

  const typeBadge = (t) => (
    <span style={{
      background: t === 'ad' ? '#0ea5e9' : '#8b5cf6',
      color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
    }}>
      {t === 'ad' ? '📋 Ad' : '👤 User'}
    </span>
  );

  const pages = Math.ceil(total / 20);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0 }}>Reports <span style={{ fontSize: 14, color: '#888', fontWeight: 400 }}>({total})</span></h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            value={typeFilter}
            onChange={e => { setType(e.target.value); setPage(1); }}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}
          >
            <option value="">All Types</option>
            <option value="user">User Reports</option>
            <option value="ad">Ad Reports</option>
          </select>
          <select
            value={statusFilter}
            onChange={e => { setStatus(e.target.value); setPage(1); }}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>
      </div>

      {loading ? <div className={styles.center}>Loading...</div> : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Type</th>
                <th>Reported</th>
                <th>Reported By</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 && (
                <tr><td colSpan={7} className={styles.empty}>No reports found</td></tr>
              )}
              {reports.map(r => (
                <tr key={r._id}>
                  <td>{typeBadge(r.type)}</td>
                  <td>
                    {r.type === 'user' ? (
                      <>
                        <strong>{r.reportedUser?.name || 'Deleted'}</strong>
                        <br /><small style={{ color: '#999' }}>{r.reportedUser?.email}</small>
                      </>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {r.reportedAd?.images?.[0] && (
                          <img src={imgUrl(r.reportedAd.images[0])} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }} />
                        )}
                        <span style={{ fontSize: 13 }}>{r.reportedAd?.title || 'Deleted Ad'}</span>
                      </div>
                    )}
                  </td>
                  <td>
                    {r.reportedBy?.name || '—'}
                    <br /><small style={{ color: '#999' }}>{r.reportedBy?.email}</small>
                  </td>
                  <td style={{ fontSize: 13 }}>{r.reason}</td>
                  <td>{statusBadge(r.status)}</td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {new Date(r.createdAt).toLocaleDateString()}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {r.status === 'pending' && (
                        <button className={styles.editBtn} onClick={() => handleReview(r._id)}>
                          Review
                        </button>
                      )}
                      {r.type === 'user' && r.status !== 'blocked' && (
                        <button className={styles.deleteBtn} onClick={() => handleBlock(r._id)}>
                          Block User
                        </button>
                      )}
                      {r.type === 'ad' && r.status !== 'blocked' && (
                        <button className={styles.deleteBtn} onClick={() => handleRemoveAd(r._id)}>
                          Remove Ad
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center' }}>
          <button className={styles.filterBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span style={{ lineHeight: '32px', fontSize: 13 }}>Page {page} of {pages}</span>
          <button className={styles.filterBtn} disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}
